import { ref, onUnmounted, onMounted, nextTick } from 'vue';
import { throttle } from '@/utils/api';

/**
 * 基于 MutationObserver 的高度变化监听 Hook
 * @param {Object} options 配置选项
 * @param {Function} options.onHeightChange 高度变化回调函数
 * @param {Number} options.throttleDelay 节流延迟时间（毫秒），默认 150
 * @param {Ref} options.targetRef 目标元素的 ref 引用，提供后会自动在 onMounted 时开始观察
 * @returns {Object} 返回观察器控制对象
 */
export function useHeightObserver(options = {}) {
	const { onHeightChange = () => {}, throttleDelay = 150, targetRef = null, manual = false } = options;

	const observer = ref(null);
	const targetElement = ref(null);
	const currentHeight = ref(0);

	/**
	 * 获取元素高度
	 */
	const getElementHeight = element => {
		if (!element) return 0;
		return element.getBoundingClientRect().height;
	};

	/**
	 * 高度变化处理函数
	 */
	const handleHeightChange = throttle(async () => {
		if (!targetElement.value) return;

		await nextTick();

		const height = getElementHeight(targetElement.value);
		const heightChanged = Math.abs(height - currentHeight.value) > 0.1;

		if (heightChanged) {
			const previousHeight = currentHeight.value;
			currentHeight.value = height;

			// 调用回调函数
			onHeightChange({
				height,
				previousHeight,
				element: targetElement.value
			});
		}
	}, throttleDelay);

	/**
	 * 开始观察指定元素
	 */
	const observe = element => {
		if (!element) {
			console.warn('useHeightObserver: 目标元素不存在');
			return;
		}

		// 停止之前的观察
		disconnect();

		targetElement.value = element;
		currentHeight.value = getElementHeight(element);

		// 创建 MutationObserver
		observer.value = new MutationObserver(mutations => {
			let shouldUpdate = false;

			mutations.forEach(mutation => {
				if (mutation.type === 'childList') {
					// 子节点发生变化
					shouldUpdate = true;
				} else if (mutation.type === 'attributes') {
					// 属性发生变化，特别是可能影响高度的属性
					const attrName = mutation.attributeName;
					if (attrName === 'style' || attrName === 'class') {
						shouldUpdate = true;
					}
				} else if (mutation.type === 'characterData') {
					// 文本内容发生变化
					shouldUpdate = true;
				}
			});

			if (shouldUpdate) {
				handleHeightChange();
			}
		});

		// 开始观察
		observer.value.observe(element, {
			childList: true,
			subtree: true, // 观察所有后代节点
			attributes: true,
			characterData: true,
			attributeFilter: ['style', 'class'] // 只观察可能影响高度的属性
		});

		// console.log('useHeightObserver: 开始观察元素高度变化', element);
	};

	/**
	 * 停止观察
	 */
	const disconnect = () => {
		if (observer.value) {
			observer.value.disconnect();
			observer.value = null;
		}
		targetElement.value = null;
		// console.log('useHeightObserver: 停止观察');
	};

	/**
	 * 手动触发高度检查
	 */
	const checkHeight = () => {
		handleHeightChange();
	};

	// 组件挂载时自动开始观察（如果提供了 targetRef）
	onMounted(() => {
		if (manual) return;
		if (targetRef && targetRef.value) {
			nextTick(() => {
				observe(targetRef.value);
			});
		}
	});
	const start = () => {
		if (targetRef && targetRef.value) {
			nextTick(() => {
				observe(targetRef.value);
			});
		}
	};

	// 组件销毁时清理
	onUnmounted(() => {
		disconnect();
	});

	return {
		start,
		observe,
		disconnect,
		checkHeight,
		currentHeight,
		isObserving: () => observer.value !== null
	};
}
